import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { SharedService } from '../../services/shared.service';
import { StartupService } from '../../../services/startup.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  standalone: true,
  imports: [ReactiveFormsModule],
})
export class ChangePasswordComponent implements OnInit {
  @Input() userId: string = '';
  @Input() resetToken: string = '';
  @Input() isForced: boolean = false; // true = first-login or forgot password reset

  form: FormGroup;
  isSubmitting = false;
  showNew = false;
  showConfirm = false;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private sharedService: SharedService,
    private startupService: StartupService,
    private router: Router,
    public modalRef: BsModalRef,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      { validators: this.passwordsMatch }
    );
  }

  passwordsMatch(group: FormGroup) {
    const pw = group.get('newPassword')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pw === confirm ? null : { mismatch: true };
  }

  hasSpecialChar(password: string): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(password);
  }

  get passwordErrors(): string[] {
    const pw = this.form.get('newPassword')?.value || '';
    const errs: string[] = [];
    if (pw.length > 0 && pw.length < 8) errs.push('At least 8 characters');
    if (pw.length >= 8 && !this.hasSpecialChar(pw)) errs.push('At least one special character');
    if (this.form.errors?.['mismatch'] && this.form.get('confirmPassword')?.value)
      errs.push('Passwords do not match');
    return errs;
  }

  submit(): void {
    if (this.form.invalid) return;
    this.isSubmitting = true;
    const newPassword = this.form.get('newPassword')?.value;

    if (this.resetToken) {
      // Use token-based reset flow
      this.startupService.resetPassword(this.resetToken, newPassword).subscribe({
        next: () => this.handleSuccess(),
        error: (err) => this.handleError(err)
      });
    } else if (this.userId) {
      // Use standard change password flow
      this.http
        .put(`http://localhost:3000/api/users/${this.userId}/change-password`, { newPassword })
        .subscribe({
          next: () => this.handleSuccess(),
          error: (err) => this.handleError(err)
        });
    }
  }

  private handleSuccess() {
    this.isSubmitting = false;
    this.sharedService.raiseSuccessToast('Password changed successfully!');
    if (this.sharedService.logged_in_user) {
      (this.sharedService.logged_in_user as any).must_change_password = false;
    }
    this.modalRef.hide();
    // Clear URL query parameters
    this.router.navigate([], { queryParams: { resetToken: null }, queryParamsHandling: 'merge' });
  }

  private handleError(err: any) {
    this.isSubmitting = false;
    this.sharedService.raiseErrorToast(err.error?.message || 'Failed to change password');
  }
}
